import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';

import State from 'Helpers/State';
import GeneralUtils from 'Helpers/GeneralUtils';
import { createOrder, giveawayOrder } from 'Redux/ducks/checkout';
import AttributionUtils from 'Helpers/Attribution/attribution-utils';
import {
  toggleLoginModal,
  toggleGiveawayEmailRequiredModal,
  toggleVoucherEmailRequiredModal,
  toggleEpicGenbaModal,
  toggleSteamModal,
  toggleGiveawayActionModal,
} from 'Redux/ducks/modal';
import {
  removeVoucher,
} from 'Redux/ducks/voucher';
import store, { history } from 'Redux/store';
import { refreshAuth } from 'Redux/ducks/auth';
import calculationSelector from 'Redux/reselects/order-calc/calculation-selector';
import { cartUnitCountSelector } from 'Redux/reselects/order-calc/cart-readers';
import { cartcalcOrderFields } from 'Helpers/Checkout/order-calc-checkout';
import { countryCode } from 'Helpers/Geo/Country';
import showRegionAvailability from 'Helpers/Checkout/region-availability';
import { locationOrderFields } from 'Helpers/Geo/location-drift';
import getEmailSubscribeStatus from 'Helpers/CRM/email-subscribe-status';
import { GIVEAWAY_CAMPAIGN_SLUG } from 'Redux/ducks/giveaway-actions';

const CheckoutUtils = {};

CheckoutUtils.creatingOrder = false;
CheckoutUtils.authChecking = false;

CheckoutUtils.authComplete = function authComplete() {
  if (!this.creatingOrder && this.authChecking) {
    this.authChecking = false;
    this.createPayment();
  }
};

CheckoutUtils.billingComplete = function billingComplete() {
  if (this.waitingForBilling) {
    this.waitingForBilling = false;
    this.createPayment();
  }
};

CheckoutUtils.getGiveawayProducts = function getGiveawayProducts() {
  const cartNotCartcalc = State.getState('cart');
  const giveawayProducts = [];
  cartNotCartcalc.items.forEach((item) => {
    if (item.giveaway) {
      if (item.type === 'bundle' && item.tierIndex === 0) {
        giveawayProducts.push(item);
      }
      if (item.type !== 'bundle') {
        giveawayProducts.push(item);
      }
    }
  });
  return giveawayProducts;
};

CheckoutUtils.getGiveawayProductIds = function getGiveawayProductIds() {
  return CheckoutUtils.getGiveawayProducts().map(item => item._id);
};

CheckoutUtils.checkIfLoggedIn = async function checkIfLoggedIn(page) {
  const auth = State.getState('auth');
  if (auth && !auth.authenticated) {
    store.dispatch(toggleLoginModal(page));
    this.authChecking = true;
    return false;
  }
  await store.dispatch(refreshAuth());
  return true;
};

CheckoutUtils.checkForEpicAccount = async function checkForEpicAccount(items) {
  const auth = State.getState('auth');

  if (items.find(item => item.source === 'epicGenba')) {
    if (auth && auth.epic && auth.epic.id) {
      return true;
    }

    store.dispatch(toggleEpicGenbaModal());
    return false;
  }
  return true;
};

CheckoutUtils.checkForCampaigns = function checkForCampaigns() {
  const campaignGiveawayProducts = this
    .getGiveawayProducts()
    .filter(i => i.slug === GIVEAWAY_CAMPAIGN_SLUG);
  const giveawayActions = State.getState('giveawayActions') || [];

  if (campaignGiveawayProducts?.length === 0) {
    return true;
  }

  const missingCampaignActions = campaignGiveawayProducts
    .filter(p => !giveawayActions.find(action => action.slug === p.slug && action.visitedCampaign));


  if (missingCampaignActions.length > 0) {
    const product = missingCampaignActions[0];
    store.dispatch(toggleGiveawayActionModal({
      type: 'VisitCampaign',
      name: product.name,
      slug: product.slug,
    }),
    );
    return false;
  }

  return true;
};

CheckoutUtils.checkForSteam = function checkForSteam() {
  const steamGiveawayProducts = this.getGiveawayProducts().filter(i => i.giveaway_steam);
  const giveawayWishlistOnSteamProducts = this.getGiveawayProducts()
    .filter(i => i.giveaway_wishlist_on_steam);

  const giveawaySteamSyncProducts = this
    .getGiveawayProducts().filter(i => i.giveaway_steam_sync || i.giveaway_wishlist_on_steam);

  if (steamGiveawayProducts.length === 0) {
    return true;
  }

  const auth = State.getState('auth');
  const giveawayActions = State.getState('giveawayActions') || [];

  if (!get(auth, 'steam.steam_id') || get(auth, 'steam.isLimitedAccount')) {
    store.dispatch(toggleSteamModal());
    return false;
  }

  const missingWishlistActions = giveawayWishlistOnSteamProducts
    .filter(p => !giveawayActions.find(action => action.slug === p.slug && action.wishedOnSteam));

  if (missingWishlistActions.length > 0) {
    const product = missingWishlistActions[0];
    store.dispatch(toggleGiveawayActionModal({
      type: 'WishlistOnSteam',
      name: product.giveaway_wishlist_on_steam_name || product.name,
      url: product.giveaway_wishlist_on_steam_url,
      slug: product.slug,
    }),
    );
    return false;
  }

  const missingSteamSyncActions = giveawaySteamSyncProducts
    .filter(p => !giveawayActions.find(ga => ga.slug === p.slug && ga.syncedSteamWishlist));

  if (missingSteamSyncActions.length > 0) {
    const product = missingSteamSyncActions[0];
    store.dispatch(toggleGiveawayActionModal({
      type: 'SteamSync',
      slug: product.slug,
    }),
    );
    return false;
  }

  return true;
};

const hasGivenConsent = () => {
  const { isSubscribed, hasLocalConsent } = getEmailSubscribeStatus(State.getState('auth'));
  return isSubscribed || hasLocalConsent;
};

CheckoutUtils.checkForNewsletter = function checkForNewsletter() {
  const newsletterGiveawayProducts = this.getGiveawayProducts().filter(i => i.giveaway_newsletter);
  if (newsletterGiveawayProducts.length > 0) {
    if (hasGivenConsent()) {
      return true;
    }
    store.dispatch(toggleGiveawayEmailRequiredModal());
    return false;
  }
  const voucher = State.getState('voucher');
  const newsletter = get(voucher, 'voucher.newsletter');
  if (newsletter && !hasGivenConsent()) {
    store.dispatch(toggleVoucherEmailRequiredModal());
    // Re-read: the modal may have set local consent
    return hasGivenConsent();
  }
  return true;
};

CheckoutUtils.cancelAndRemoveVoucher = async function cancelAndRemoveVoucher() {
  await store.dispatch(removeVoucher());
  store.dispatch(toggleVoucherEmailRequiredModal());
};

CheckoutUtils.goToPaymentPage = () => {
  history.push('/payment');
};

CheckoutUtils.doGiveawayPayment = (orderId, cartUnitCount, giveawayProductIds) => {
  if (
    giveawayProductIds.length > 0 &&
    cartUnitCount === giveawayProductIds.length
  ) {
    store.dispatch(giveawayOrder(orderId));
  }
};

CheckoutUtils.createPayment = async function createPayment() {
  const state = State.getState();
  // order-calc Stage 6.3: the cart has no total, so there is no order
  if (calculationSelector(state).error) {
    return false;
  }

  this.creatingOrder = true;
  const checkIfLoggedIn = await this.checkIfLoggedIn('checkout');
  if (!checkIfLoggedIn) {
    return false;
  }
  const checkForEpicAccount = await this.checkForEpicAccount(get(state, 'cart.items', []));
  if (!checkForEpicAccount) {
    return false;
  }

  this.creatingOrder = false;

  const checkForNewsletter = this.checkForNewsletter();
  if (!checkForNewsletter) {
    return false;
  }

  const checkForSteam = this.checkForSteam();
  if (!checkForSteam) {
    return false;
  }

  const checkForCampaigns = this.checkForCampaigns();
  if (!checkForCampaigns) {
    return false;
  }

  CheckoutUtils.continueToPayment();
  return true;
};

CheckoutUtils.continueToPayment = async function createPayment() {
  const lastSeenCoBranding = localStorage.getItem('lastSeenCoBranding');
  const today = new Date().getTime();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  const state = State.getState();
  const { order: orderCalc, error: orderCalcError } = calculationSelector(state);
  // Also here: the newsletter consent continues to payment without createPayment
  if (orderCalcError) {
    return false;
  }

  const voucher = state.voucher;
  const actualVoucher = get(voucher, 'voucher', {});
  let voucherCode = get(actualVoucher, 'code', '');
  const voucherText = get(voucher, 'voucherText', '');
  if (actualVoucher.discount_type === 'Account' || actualVoucher.discount_type === 'Unique') voucherCode = voucherText;
  const { locale } = window;

  const orderOptions = {
    snowplowId: GeneralUtils.getSnowplowDomainUserId(),
    snowplowSessionId: GeneralUtils.getSnowplowSessionId(),
    ref_code: GeneralUtils.getReferalCode(),
    locale,
    giftOrder: State.getGiftOrderData(),
    ...cartcalcOrderFields(orderCalc),
    ...locationOrderFields(),
    localNewsletterConsent: state.auth.localNewsletterConsent,
  };
  const siteBrand = localStorage.getItem('siteBrand');
  if (siteBrand && lastSeenCoBranding && (today - Number(lastSeenCoBranding) < thirtyDays)) {
    orderOptions.siteBrand = siteBrand;
  }

  if (orderCalc.totals.total !== 0 || actualVoucher.type_of_discount === 'money') {
    orderOptions.code = voucherCode;
  }

  orderOptions.attribution = AttributionUtils.getAttribution();

  const giveawayProductIds = this.getGiveawayProductIds();
  // orderCalc: the New Relic createOrder event compares the order with it
  const orderData = await store.dispatch(createOrder(orderOptions, orderCalc));

  if (orderData.type && orderData.type === 'billing') {
    this.waitingForBilling = true;
    store.dispatch({ type: 'frontend/checkout/BILLING_DETAILS_REQUESTED', payload: orderData.data.requiredFields });
    history.push('/billing');
    return true;
  }

  if (isEmpty(orderData.order) || !get(orderData, 'order._id', false)) {
    return false;
  }


  if (orderData.order.items.every(item => item.giveaway || get(item, 'report.free_product_id')) &&
    orderData.order.payment &&
    orderData.order.payment.total === 0) {
    return this.doGiveawayPayment(
      orderData.order._id,
      cartUnitCountSelector(state),
      giveawayProductIds,
    );
  }

  const apiPaymentData = get(orderData, 'paymentData', {});

  if (apiPaymentData.billingFields) {
    store.dispatch({ type: 'frontend/checkout/BILLING_DETAILS_REQUESTED', payload: apiPaymentData.billingFields });
  }

  if (orderData.isAmountVoucher) {
    // Don't need stripe setup if user is paying fully with voucher
    // TODO use a separate flow for this, no point loading stripe
    return this.goToPaymentPage();
  }

  if (apiPaymentData.stripeRedirectUrl) {
    return window.location.assign(apiPaymentData.stripeRedirectUrl);
  }

  if (!apiPaymentData.stripeClientSecret) {
    throw new Error('Missing stripeClientSecret');
  }

  return this.goToPaymentPage();
};

CheckoutUtils.openWindowWithPostData = (url, data) => {
  const form = document.createElement('form');
  form.target = '_self';
  form.method = 'POST';
  form.action = url;
  form.style.display = 'none';
  form.enctype = 'application/x-www-form-urlencoded';

  const keys = Object.keys(data);

  keys.forEach((key) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = data[key];
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

CheckoutUtils.openWindow = (url) => {
  window.open(url, '_self');
};

CheckoutUtils.getAwinProductCategory = (item) => {
  const revenue = get(item, 'report.revenue', -1);
  if (revenue === 0) return 'Free Promotional Item';

  if (item.pickAndMix) return 'Bundle/Pick & Mix/Pack';

  const type = get(item, 'type');
  const displayType = item.display_type || item.type;

  if (['software-bundle', 'software', 'audio-bundle', 'audio'].includes(displayType)) return 'Software';
  if (['comic-bundle', 'comic'].includes(displayType)) return 'Comic';
  if (['book', 'elearning-bundle'].includes(type) || ['book', 'elearning', 'elearning-bundle'].includes(displayType)) return 'eBook/eLearning';

  const promotionName = item.promotion;
  if (promotionName === 'Capcom') return 'Store';
  if (promotionName && promotionName !== '') return 'Bundle/Pick & Mix/Pack';

  if (type === 'bundle' && item.mystery_upsell) return 'Cart Upsell';
  if (type === 'bundle' && item.mysteryV2) return 'Mystery Bundle';
  if (type === 'bundle' && item.is_srp_bundle) return 'Store';

  if (type === 'bundle') {
    const name = get(item, 'name', '');
    if (name.includes('bundle') || name.includes('pack') || name.includes('collection')) {
      return 'Bundle/Pick & Mix/Pack';
    }
  } else {
    // Disabled to match backend awin-api.js fetchProductCategoryFromItem structure exactly
    // eslint-disable-next-line no-lonely-if
    if (get(item, 'preorder', false)) return 'Preorder/New Release';
  }

  if (item.epayId) return 'Giftcard';

  return 'Store';
};

CheckoutUtils.toQuantityItems = (items) => {
  const returnItems = [];
  const groupedItemsObject = groupBy(items, '_id');
  items.forEach((item) => {
    const returnItem = Object.assign({}, item);
    returnItem.quantity = groupedItemsObject[item._id].length;
    returnItems.push(returnItem);
  });
  return returnItems;
};

CheckoutUtils.getCompleteOrderTransaction = (items) => {
  const returnObject = { transactionTax: 0, transactionProducts: [] };
  (items || []).forEach((item) => {
    const newObject = {
      sku: '',
      name: '',
      category: '',
      price: 0,
      quantity: 0,
      local_price: 0,
      supplier_ids: [],
    };
    if (item._id) {
      newObject.sku = item._id;
    }
    if (item.name) {
      newObject.name = item.name;
    }
    if (item.supplier_id) {
      newObject.supplier_ids.push(item.supplier_id);
    }
    if (item.bundles.length) {
      item.bundles.forEach((tiers) => {
        tiers.games.forEach((game) => {
          newObject.supplier_ids.push(game.supplier_id);
        });
      });
    }
    if (item.type) {
      newObject.category = item.type;
    }
    if (item.display_type) {
      newObject.display_type = item.display_type;
    }
    let price = 0;
    const quantity = 1;
    if (item.report) {
      if (item.report.total) {
        price = item.report.total;
      }
      if (item.report.vat) {
        returnObject.transactionTax += item.report.vat;
      }
    }
    let localPrice = 0;
    if (item.payment) {
      if (item.payment.total) {
        localPrice = item.payment.total;
      }
    }
    newObject.price = price;
    newObject.itemTax = item.report.vat;
    newObject.quantity = quantity;
    newObject.local_price = localPrice;
    newObject.discountPercent = get(item, 'discount.percent');
    newObject.fullPrice = get(item, 'report.pickAndMixOriginalPrice'); // unusual - only reference to full price - used in other places in the system
    returnObject.transactionProducts.push(newObject);
  });
  return returnObject;
};


// The cart items are Redux basket objects: this reads them and writes nothing.
// (It used to set item.isRegionExcluded on them. Nothing reads that flag.)
CheckoutUtils.evaluateCartItems = items => ({
  showRegionAvailablility: showRegionAvailability(items, countryCode),
});

export default CheckoutUtils;
